// marriedBoard.js — the REAL cross-book married board (CF-KV plane), adapted to the row shape the
// LiveOddsTableWidget already renders. This is the same 33-book married board the proof portal shows
// (/crossbook-allbets) and the +EV/arb widgets consume — NOT the thin Supabase snapshot. One row per
// (book · market · selection · line); the widget groups these into games with a column per book.
//
// Board rows (cat_game-lines.json) carry: book, event "AWAY @ HOME", et_date, et_time, league, bet_type,
// selection, line, american, is_primary, state, period. We map them to the board_snapshot fields the
// widget expects (BOARD_SELECT_FIELDS). Prices/lines are passed VERBATIM — never cleaned.

const BOARD_BASE = 'https://abet-board-data.abetco.workers.dev/data';

const MARKET_MAP = {
  Moneyline: 'MONEYLINE',
  Spread: 'SPREAD',
  'Run Line': 'SPREAD',
  'Puck Line': 'SPREAD',
  Total: 'TOTAL',
};

// Board rows carry et_date ("2026-07-16") + et_time ("3:00 PM ET") but no raw ISO start. Reconstruct a
// UTC start so the widget's start-time window (6h past → 370d future) keeps TODAY's games instead of
// dropping them as "midnight = 18h old". ET is UTC-4 in season (EDT); approximate is fine for windowing.
function toStartIso(etDate, etTime) {
  if (!etDate) return null;
  const parts = String(etDate).split('-').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
  const [y, mo, d] = parts;
  let h = 12;
  let min = 0;
  const m = String(etTime || '').match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (m) {
    h = parseInt(m[1], 10) % 12;
    if (/PM/i.test(m[3])) h += 12;
    min = parseInt(m[2], 10);
  }
  return new Date(Date.UTC(y, mo - 1, d, h + 4, min)).toISOString(); // +4h ET→UTC (EDT)
}

function adaptRow(r, generatedAt) {
  const market = MARKET_MAP[r.bet_type];
  if (!market) return null; // only game lines onto the odds table (props/futures handled elsewhere)
  const parts = String(r.event || '').split(' @ ');
  if (parts.length !== 2) return null;
  const [away, home] = parts;
  const line = r.line == null ? null : r.line;
  return {
    compound_key: `${r.league}|${r.event}|${r.et_date}|${r.bet_type}|${r.selection}|${line ?? ''}`,
    canonical_id: `${r.league}|${r.event}|${r.et_date}`,
    participants: r.event,
    away_team: away,
    home_team: home,
    start_time: r.start || r.start_utc || toStartIso(r.et_date, r.et_time) || r.et_date, // real game time (windowing)
    league: r.league,
    bookmaker: r.book,
    market_normalized: market,
    selection: r.selection,
    american_odds: r.american, // VERBATIM
    handicap: line, // VERBATIM
    is_live: r.state === 'live',
    game_status: r.state || null,
    home_score: null,
    away_score: null,
    period: r.period || null,
    clock: null,
    updated_at: generatedAt,
    is_primary: r.is_primary === true,
  };
}

// Fetch + adapt the live married board. Returns { rows, generatedAt, bookCount, source }.
export async function fetchMarriedBoardRows() {
  const res = await fetch(`${BOARD_BASE}/cat_game-lines.json`, { cache: 'no-store' });
  if (!res.ok) throw new Error(`married board HTTP ${res.status}`);
  const data = await res.json();
  const raw = Array.isArray(data?.rows) ? data.rows : [];
  const generatedAt = data?.generated_utc || data?.captured_utc || new Date().toISOString();
  const rows = raw.map((r) => adaptRow(r, generatedAt)).filter(Boolean);
  const bookCount = new Set(rows.map((r) => r.bookmaker)).size;
  return { rows, generatedAt, bookCount, source: 'married-board:cf-kv' };
}
